import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import BookingForm from './BookingForm';

function EmbeddedWorkshop() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [embedSettings, setEmbedSettings] = useState({
    borderRadius: 12,
    primaryColor: '#FFB27D',
    backgroundColor: '#1A1A1A',
    textColor: '#FFFFFF',
    fontFamily: 'Inter',
    showImage: true,
    showPrice: true,
    showDate: true,
    showLocation: true,
    width: '100%',
    height: 'auto'
  });

  useEffect(() => {
    fetchWorkshop();
    parseEmbedSettings();
  }, [id, searchParams]);

  const parseEmbedSettings = () => {
    try {
      const settingsParam = searchParams.get('settings');
      if (settingsParam) {
        const decodedSettings = JSON.parse(decodeURIComponent(settingsParam));
        setEmbedSettings(prev => ({ ...prev, ...decodedSettings }));
      }
    } catch (error) {
      console.error('Error parsing embed settings:', error);
    }
  };

  const fetchWorkshop = async () => {
    try {
      const workshopDoc = await getDoc(doc(db, 'sessions_workshops', id));
      
      if (!workshopDoc.exists()) {
        setError('Workshop not found');
        return;
      }

      const workshopData = { id: workshopDoc.id, ...workshopDoc.data() };
      setWorkshop(workshopData);
    } catch (error) {
      console.error('Error fetching workshop:', error);
      setError('Error loading workshop');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'TBD';
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'TBD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getLowestPrice = () => {
    if (workshop.packages && workshop.packages.length > 0) {
      const prices = workshop.packages
        .map(pkg => parseFloat(pkg.price))
        .filter(price => !isNaN(price));
      return prices.length > 0 ? Math.min(...prices) : null;
    }
    return workshop.price || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8" style={{ backgroundColor: embedSettings.backgroundColor }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: embedSettings.primaryColor }}></div>
          <p style={{ color: embedSettings.textColor }}>Loading workshop...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center" style={{ backgroundColor: embedSettings.backgroundColor }}>
        <p style={{ color: embedSettings.textColor }}>{error}</p>
      </div>
    );
  }

  if (!workshop) {
    return (
      <div className="p-8 text-center" style={{ backgroundColor: embedSettings.backgroundColor }}>
        <p style={{ color: embedSettings.textColor }}>Workshop not found</p>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: embedSettings.backgroundColor,
        color: embedSettings.textColor,
        fontFamily: embedSettings.fontFamily
      }}
    >
      {showBookingForm ? (
        <div className="p-6">
          <div className="max-w-2xl mx-auto">
            <div className="mb-6">
              <button
                onClick={() => setShowBookingForm(false)}
                className="flex items-center text-sm mb-4 hover:opacity-80 transition-opacity"
                style={{ color: embedSettings.primaryColor }}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to workshop details
              </button>
              <h1 className="text-2xl font-bold mb-2">{workshop.title}</h1>
              <p className="text-sm opacity-80">Complete your booking</p>
            </div>
            <BookingForm 
              workshop={workshop} 
              onSuccess={() => {
                // Handle successful booking
                alert('Booking completed successfully!');
                setShowBookingForm(false);
              }}
              onCancel={() => setShowBookingForm(false)}
            />
          </div>
        </div>
      ) : (
        <div className="p-6">
          <div className="max-w-2xl mx-auto">
            {/* Workshop Image */}
            {embedSettings.showImage && workshop.imageUrl && (
              <div className="mb-6">
                <img 
                  src={workshop.imageUrl} 
                  alt={workshop.title}
                  className="w-full h-64 object-cover rounded-lg"
                  style={{ borderRadius: `${embedSettings.borderRadius}px` }}
                />
              </div>
            )}
            
            {/* Workshop Title */}
            <h1 className="text-3xl font-bold mb-4">{workshop.title}</h1>
            
            {/* Workshop Description */}
            <p className="text-lg mb-6 opacity-90 leading-relaxed">
              {workshop.shortDescription || workshop.description?.substring(0, 200) + '...'}
            </p>
            
            {/* Workshop Details */}
            <div className="space-y-4 mb-8">
              {embedSettings.showDate && (
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{formatDate(workshop.startDate)} - {formatDate(workshop.endDate)}</span>
                </div>
              )}
              
              {embedSettings.showLocation && (
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{workshop.location}</span>
                </div>
              )}
              
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span>{workshop.currentParticipants || 0} / {workshop.maxParticipants} participants</span>
              </div>
            </div>
            
            {/* Pricing and Booking */}
            <div className="flex items-center justify-between p-6 rounded-lg border" style={{ 
              borderColor: embedSettings.primaryColor + '30',
              backgroundColor: embedSettings.backgroundColor + '80'
            }}>
              <div>
                {embedSettings.showPrice && (
                  <div className="text-3xl font-bold mb-2" style={{ color: embedSettings.primaryColor }}>
                    {formatPrice(getLowestPrice())}
                  </div>
                )}
                <p className="text-sm opacity-80">per person</p>
              </div>
              
              <button 
                onClick={() => setShowBookingForm(true)}
                className="px-8 py-3 rounded-lg font-semibold text-black transition-all hover:opacity-90"
                style={{ 
                  backgroundColor: embedSettings.primaryColor,
                  borderRadius: `${embedSettings.borderRadius}px`
                }}
              >
                Book Now
              </button>
            </div>
            
            {/* What's Included */}
            {workshop.included && workshop.included.length > 0 && (
              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">What's Included</h3>
                <ul className="space-y-2">
                  {workshop.included.map((item, index) => (
                    <li key={index} className="flex items-center">
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: embedSettings.primaryColor }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EmbeddedWorkshop;
